import type { TPartnerStatus } from 'legacy/lut/classes/models/partner360/IFormPartnerBasicInfo'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Tooltip } from 'core-components/library/tooltip'
import { Card } from 'partner360/components/card'
import { CopiedText } from 'shared/components/copiedText'

import { CockpitCardHeader } from '../../../components/cockpitCardHeader'
import { CockpitIconButton } from '../../../components/cockpitIconButton'
import { CockpitTableColumnTitle } from '../../../components/cockpitTableTitle'
import { PartnerClassList } from '../partnerClassList/PartnerClassList'
import { PartnerDetailsTitle } from '../partnerDetailsTitle/PartnerDetailsTitle'
import { PARTNER_EXTENDED_INFO_ID } from '../partnerExtendedInfo/PartnerExtendedInfo'

import styles from './PartnerSummaryCard.module.scss'

type PartnerSummaryCardProps = {
  partnerName: string
  /** The partner's market short name and country, e.g. `UK&I, United Kingdom`. */
  regionLabel: string
  partnerId: string
  locationId: string
  grades: TPartnerStatus[]
  memberships: string[]
  specializations: TPartnerStatus[]
  programs: TPartnerStatus[]
  /** Whether the drawer with the partner's extended info is open. */
  isExtendedInfoOpen: boolean
  onExtendedInfoToggle: () => void
  /** `data-tour-id` for the card, from the cockpit whose tour points at it. */
  tourId?: string
}

type PartnerStatusChipsProps = {
  items: TPartnerStatus[]
}

const EmptyValue = () => <span className={styles.empty}>--</span>

const PartnerStatusChips = ({ items }: PartnerStatusChipsProps) => {
  if (!items.length) {
    return <EmptyValue />
  }

  return (
    <>
      {items.map((status, index) => (
        <Tooltip
          key={`${status.Title}:${index}`}
          tipContent={() => <Translation text={status.Info} span />}
        >
          <span className={styles.chip} aria-label={status.Title}>
            {status.Title}
          </span>
        </Tooltip>
      ))}
    </>
  )
}

/** The partner at a glance, in one card worn like a tile: its name, market and country in the
 *  header, with the way to its extended info on the right; its IDs and classes, then its Amplify
 *  membership, specialisations and programs under it. */
export const PartnerSummaryCard = ({
  partnerName,
  regionLabel,
  partnerId,
  locationId,
  grades,
  memberships,
  specializations,
  programs,
  isExtendedInfoOpen,
  onExtendedInfoToggle,
  tourId,
}: PartnerSummaryCardProps) => {
  return (
    <Card
      className={styles.card}
      hoverable={false}
      data-tour-id={tourId}
      header={
        <CockpitCardHeader
          title={
            <Tooltip tipContent={() => <>{partnerName || '--'}</>}>
              <span className={styles.name}>{partnerName || '--'}</span>
            </Tooltip>
          }
          description={regionLabel || '--'}
          actions={
            <CockpitIconButton
              icon="kbase kbase-info"
              label={isExtendedInfoOpen ? 'Hide extended info' : 'Show extended info'}
              expanded={isExtendedInfoOpen}
              ariaControls={PARTNER_EXTENDED_INFO_ID}
              onClick={onExtendedInfoToggle}
            />
          }
        />
      }
    >
      {/* Two facts side by side, as the extended info prints them: the label over the value, and
          only the value is copied. */}
      <dl className={styles.ids}>
        <div className={styles.id}>
          <dt>
            <CockpitTableColumnTitle>PID</CockpitTableColumnTitle>
          </dt>
          <dd className={styles.idValue}>
            {partnerId ? <CopiedText>{partnerId}</CopiedText> : '--'}
          </dd>
        </div>
        <div className={styles.id}>
          <dt>
            <CockpitTableColumnTitle>Loc ID</CockpitTableColumnTitle>
          </dt>
          <dd className={styles.idValue}>
            {locationId ? <CopiedText>{locationId}</CopiedText> : '--'}
          </dd>
        </div>
      </dl>

      <PartnerClassList grades={grades} />

      <div className={styles.divider} />

      <div className={styles.profile}>
        <div>
          <PartnerDetailsTitle>Amplify membership</PartnerDetailsTitle>
          {memberships.length ? (
            <div className={styles.chips}>
              {memberships.map((membership, index) => (
                <span key={membership} className={index === 0 ? styles.chip : styles['chip-solid']}>
                  {membership}
                </span>
              ))}
            </div>
          ) : (
            <EmptyValue />
          )}
        </div>

        <div>
          <PartnerDetailsTitle>Specialisations</PartnerDetailsTitle>
          <div className={styles.chips}>
            <PartnerStatusChips items={specializations} />
          </div>
        </div>

        <div>
          <PartnerDetailsTitle>Programs</PartnerDetailsTitle>
          <div className={styles.chips}>
            <PartnerStatusChips items={programs} />
          </div>
        </div>
      </div>
    </Card>
  )
}

PartnerSummaryCard.displayName = 'PartnerSummaryCard'
